// src/components/AllocationDonutChart.jsx - Simplified with cleaner styling
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // green-500
  '#6366F1', // indigo-500
  '#EC4899', // pink-500
  '#8B5CF6', // purple-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#06B6D4', // cyan-500
];

const AllocationDonutChart = ({ 
  positions = [], 
  unallocatedEthBalance = "0",
  ethPrice = 0
}) => {
  // Safety check for positions
  if (!positions || !Array.isArray(positions) || positions.length === 0) {
    return (
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm h-80 flex items-center justify-center">
        <p className="text-gray-500 italic">No positions data available for chart</p>
      </div>
    );
  }

  // Only include positions with values
  const positionsWithValues = positions.filter(position => 
    position && position.valueUsd && position.valueUsd > 0
  );

  if (positionsWithValues.length === 0 && parseFloat(unallocatedEthBalance) <= 0) {
    return (
      <div className="rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden">
        <p className="text-gray-500 italic">No valued positions found for chart</p>
      </div>
    );
  }

  // Format data for the chart
  const data = [...positionsWithValues.map(position => ({
    name: position.name || 'Unknown',
    value: position.valueUsd || 0,
    tokenAddress: position.address || '',
    balance: position.balance || '0',
    allocation: position.allocation || 0
  }))];
  
  // Add unallocated ETH if available
  if (parseFloat(unallocatedEthBalance) > 0 && ethPrice > 0) {
    const unallocatedValueUsd = parseFloat(unallocatedEthBalance) * ethPrice;
    data.push({
      name: 'Unallocated ETH',
      value: unallocatedValueUsd,
      tokenAddress: 'eth',
      balance: unallocatedEthBalance,
      allocation: 0, // No target allocation for unallocated ETH
      isUnallocated: true
    });
  }

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length && payload[0].payload) {
      const item = payload[0];
      const position = item.payload;
      
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-semibold">{position.name}</p>
          <p className="text-sm">${(position.value || 0).toFixed(2)} USD</p>
          {position.isUnallocated ? (
            <p className="text-sm">{position.balance} ETH (unallocated)</p>
          ) : (
            <>
              <p className="text-sm">{position.balance} tokens</p>
              <p className="text-sm">{((position.allocation || 0) / 100).toFixed(2)}% target allocation</p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  // Calculate total value for the pie chart
  const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);

  // Return early if no total value
  if (totalValue <= 0) {
    return (
      <div className="rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden">
        <p className="text-gray-500 italic">No value data available for chart</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-4 shadow-[0_2px_4px_1px_rgba(0,0,0,0.1)] bg-white overflow-hidden">
      <h3 className="font-semibold text-lg text-gray-800 mb-4">Fund Asset Allocation</h3>
      
      <div className="pt-[64%] relative">
        <ResponsiveContainer className="absolute top-0 right-0 left-0" width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              fill="#8884d8"
              paddingAngle={2}
              dataKey="value"
              nameKey="name"
              label={({ name, percent }) => `${name || 'Unknown'} (${(percent * 100).toFixed(1)}%)`}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.isUnallocated ? '#94A3B8' : COLORS[index % COLORS.length]} 
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              align="bottom"
              formatter={(value, entry) => {
                if (!entry || !entry.payload) return value;
                const { payload } = entry;
                return (
                  <span className="text-sm">
                    {value} (${(payload.value || 0).toFixed(2)})
                  </span>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      {/* Total Fund NAV with more subtle styling */}
      <div className="mt-12 text-center">
        <div className="inline-block bg-white border border-gray-200 px-6 py-3 rounded-lg shadow-sm">
          <div className="text-sm text-gray-600 font-medium">Total Fund NAV</div>
          <div className="text-2xl font-bold text-gray-800 mt-1">${totalValue.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
};

export default AllocationDonutChart;